import type { TenantConfig } from '@/types'

import { readonly, ref } from 'vue'

let serverConfig: TenantConfig | null = null

const TENANT_ID_KEY = 'salesRepApp_tenantId'

/**
 * Load tenant configuration from the Express server.
 * Expects ?t=tenantId in the URL query string.
 * Falls back to sessionStorage (survives refresh/navigation),
 * then localStorage for local development.
 */
export async function loadServerConfig(): Promise<TenantConfig | null> {
  const params = new URLSearchParams(window.location.search)
  let tenantId = params.get('t')

  // Fall back to localStorage if ?t= is missing (refresh, new tab, or in-app navigation)
  if (!tenantId) {
    tenantId = localStorage.getItem(TENANT_ID_KEY)
  }

  if (tenantId) {
    try {
      const res = await fetch(`/api/tenant-config?t=${encodeURIComponent(tenantId)}`)
      if (res.ok) {
        serverConfig = await res.json() as TenantConfig
        // Persist tenant ID so it survives page refreshes and new tabs
        localStorage.setItem(TENANT_ID_KEY, tenantId)
        return serverConfig
      }
      console.error('[config] Tenant config not found for:', tenantId, res.status)
    } catch (e) {
      console.error('[config] Failed to load tenant config:', e)
    }
    return null
  }

  // Development fallback — store a config object in localStorage:
  // localStorage.setItem('salesRepApp_config', JSON.stringify({ tenantId:'dev', apiBaseUrl:'https://localhost:44363', apiKey:'3dc5e12f-b45c-4b02-a039-5bada77a71f9', theme:'light', portalName:'Dev Portal' }))
  // Example: add devAutoLoginEmail:'lisa@fakebrains.com' to use a different localhost auto-login user.
  const devConfig = localStorage.getItem('salesRepApp_config')
  if (devConfig) {
    try {
      serverConfig = JSON.parse(devConfig) as TenantConfig
      console.log('[config] Using dev config from localStorage')
      return serverConfig
    } catch {
      console.error('[config] Failed to parse dev config from localStorage')
    }
  }

  return null
}

export function getConfig(): TenantConfig | null {
  return serverConfig
}

export function getApiBaseUrl(): string {
  return serverConfig?.apiBaseUrl ?? ''
}

export function getApiKey(): string {
  return serverConfig?.apiKey ?? ''
}

export function getTenantId(): string {
  return serverConfig?.tenantId ?? ''
}

export function getTheme(): 'light' | 'dark' {
  return serverConfig?.theme ?? 'light'
}

export function getDevAutoLoginEmail(): string {
  return serverConfig?.devAutoLoginEmail?.trim() || 'pat@fakebrains.com'
}

export function isApiConfigured(): boolean {
  return !!(serverConfig?.apiBaseUrl && serverConfig?.apiKey)
}

export function isTenantMode(): boolean {
  return !!(new URLSearchParams(window.location.search).get('t') || localStorage.getItem(TENANT_ID_KEY))
}

export function isDemoEnabled(): boolean {
  return serverConfig?.demoEnabled === true
}

export function isDevelopment(): boolean {
  return import.meta.env.DEV
}

// ============================================================================
// Session management
// ============================================================================

const SESSION_TOKEN_KEY = 'salesRepApp_sessionToken'
const SESSION_REP_KEY = 'salesRepApp_salesRep'
const sessionTokenState = ref<string | null>(sessionStorage.getItem(SESSION_TOKEN_KEY))

export function getSessionToken(): string | null {
  return sessionTokenState.value
}

export function getSessionTokenRef() {
  return readonly(sessionTokenState)
}

export function setSessionToken(token: string): void {
  sessionStorage.setItem(SESSION_TOKEN_KEY, token)
  sessionTokenState.value = token
}

export function clearSession(): void {
  sessionStorage.removeItem(SESSION_TOKEN_KEY)
  sessionStorage.removeItem(SESSION_REP_KEY)
  localStorage.removeItem(TENANT_ID_KEY)
  sessionTokenState.value = null
}

export function isAuthenticated(): boolean {
  return !!getSessionToken()
}
